import { useCallback, useEffect, useState } from "react";
import type { IWasteMovementDwtSubmission } from "@/customTypings/wasteMovement/WasteMovement";
import { wasteMovementsService } from "@/services/wasteMovements/wasteMovements.service";
import { logErrorForDev } from "@/utils";

export type WasteMovementDwtSubmissionState =
    | { status: "loading" }
    | { status: "error" }
    | { status: "loaded"; submission: IWasteMovementDwtSubmission };

/**
 * S2B: the persisted DWT submission state of a saved Waste Received movement, as the server reports it. `reload` reads
 * it again (after a submit, or to see the outcome of one made elsewhere); `show` displays a state the server returned.
 */
export function useWasteMovementDwtSubmission(
    siteId: string | undefined,
    movementId: string | undefined,
    savedVersion: string | undefined,
    enabled: boolean
): {
    state: WasteMovementDwtSubmissionState | null;
    reload: () => void;
    show: (submission: IWasteMovementDwtSubmission) => void;
} {
    const [state, setState] = useState<WasteMovementDwtSubmissionState | null>(null);
    const [reloadToken, setReloadToken] = useState(0);

    useEffect(() => {
        if (!enabled || !siteId || !movementId) {
            setState(null);
            return;
        }
        let cancelled = false;
        setState((current) => (current?.status === "loaded" ? current : { status: "loading" }));
        wasteMovementsService
            .getDwtSubmission(siteId, movementId)
            .then((submission) => {
                if (!cancelled) setState({ status: "loaded", submission });
            })
            .catch((e: unknown) => {
                logErrorForDev("wasteMovement.dwtSubmission", e);
                if (!cancelled) setState({ status: "error" });
            });
        return () => {
            cancelled = true;
        };
    }, [siteId, movementId, savedVersion, enabled, reloadToken]);

    const reload = useCallback(() => setReloadToken((n) => n + 1), []);
    const show = useCallback((submission: IWasteMovementDwtSubmission) => setState({ status: "loaded", submission }), []);

    return { state, reload, show };
}
